import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Select } from "../shared/Select";
import { BookWrapper, SelectWrapper, TableWrapper } from "./style";
import { SPACING } from "../../constants/design/spacing";
import { RiskPositionTable } from "../Account/tables/RiskPositionTable";
import { InstrumentType, usePositions } from "../../hooks/api/admin/usePositions";
import { MarketDropdown } from "../Dropdown/MarketDropdown";
import { useMarkets } from "../../hooks/api/aevo/useMarkets";
import { DataContext } from "../../context/DataContext";
import { IAevoRiskPosition } from "../../interfaces/aevo";

// Fetches ONE asset's positions and reports them up. `usePositions` is keyed
// per (asset, instrumentType, limit) and uses SWR's immutable cache, so each
// asset's result is cached: clicking a new pill only fetches the asset(s) not
// already loaded, and re-selecting one is served instantly from cache.
// (The API returns positions per-asset + limited, so there's no single
// "all positions" payload to reuse — hence one fetch per asset.)
function AssetPositionsLoader({
  cacheKey,
  asset,
  instrumentType,
  limit,
  onResult,
}: {
  cacheKey: string;
  asset: string;
  instrumentType: InstrumentType;
  limit: number;
  onResult: (cacheKey: string, positions: IAevoRiskPosition[]) => void;
}) {
  const { data } = usePositions(instrumentType, asset, limit);
  const positions = data?.positions;
  useEffect(() => {
    onResult(cacheKey, positions ?? []);
  }, [cacheKey, positions, onResult]);
  return null;
}

export function RiskPage() {
  // Source of truth for the asset filter. Empty = all assets.
  // Both the dropdown and the pills read/write this, so they stay in sync.
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [selectedInstrumentType, setInstrumentType] = useState<InstrumentType>("NONE");

  const { data: marketsData } = useMarkets();
  const { records } = useContext(DataContext);

  // Available assets for the pills: ETH, BTC first, then the rest (deduped) —
  // mirrors MarketDropdown's ordering.
  const availableAssets = useMemo(() => {
    const rest = (marketsData || [])
      .map((m) => m.underlying_asset)
      .filter((a) => a && a !== "ETH" && a !== "BTC");
    return ["ETH", "BTC", ...Array.from(new Set(rest))];
  }, [marketsData]);

  // No selection → the global view (asset="").
  const fetchAssets = selectedAssets.length ? selectedAssets : [""];
  // Cache positions per (asset, instrumentType) so toggling pills reuses
  // already-fetched results instead of re-fetching the whole selection.
  const cacheKey = (asset: string) => `${asset}|${selectedInstrumentType}`;

  const [positionsByKey, setPositionsByKey] = useState<
    Record<string, IAevoRiskPosition[]>
  >({});
  const handleResult = useCallback(
    (key: string, positions: IAevoRiskPosition[]) =>
      setPositionsByKey((prev) =>
        prev[key] === positions ? prev : { ...prev, [key]: positions }
      ),
    []
  );

  const displayedPositions = fetchAssets.flatMap(
    (asset) => positionsByKey[cacheKey(asset)] ?? []
  );

  const toggleAsset = (asset: string) =>
    setSelectedAssets((prev) =>
      prev.includes(asset) ? prev.filter((a) => a !== asset) : [...prev, asset]
    );

  return (
    <BookWrapper>
      {/* One cached fetcher per selected asset (or the global "" view). Render
          nothing; they just populate the per-asset cache. */}
      {fetchAssets.map((asset) => (
        <AssetPositionsLoader
          key={cacheKey(asset)}
          cacheKey={cacheKey(asset)}
          asset={asset}
          instrumentType={selectedInstrumentType}
          limit={30}
          onResult={handleResult}
        />
      ))}

      <SelectWrapper>
        <MarketDropdown
          // Picking from the dropdown sets a single asset (reflected as a
          // highlighted pill); "All Assets" ("") clears to all.
          onClick={(asset) => setSelectedAssets(asset ? [asset] : [])}
          value={selectedAssets.length === 1 ? selectedAssets[0] : ""}
        />

        {/* Asset pills — click to highlight/dehighlight individual assets
            (multi-select). Shares state with the dropdown above. */}
        <Select
          wrapperStyle={{
            width: "100%",
            // Wrap pills onto the next row instead of scrolling horizontally.
            flexWrap: "wrap",
            rowGap: SPACING.one,
            marginTop: 20,
          }}
          isRound
          options={[
            {
              label: "All Assets",
              isActive: selectedAssets.length === 0,
              onClick: () => setSelectedAssets([]),
            },
            ...availableAssets.map((asset) => ({
              label: asset,
              isActive: selectedAssets.includes(asset),
              onClick: () => toggleAsset(asset),
            })),
          ]}
        />

        <Select
          wrapperStyle={{
            width: `calc(100vw - ${SPACING.three * 2}px)`,
            overflowX: "scroll",
            marginTop: 20,
          }}
          isRound
          options={[
            {
              label: "All Instrument Types",
              isActive: selectedInstrumentType === "NONE",
              onClick: () => setInstrumentType("NONE"),
            },
            ...(["OPTION", "PERPETUAL"] as InstrumentType[]).map((instrumentType) => ({
              label: instrumentType,
              isActive: selectedInstrumentType === instrumentType,
              onClick: () => setInstrumentType(instrumentType),
            })),
          ]}
        />
      </SelectWrapper>

      <TableWrapper>
        <RiskPositionTable
          records={records}
          positions={displayedPositions}
          selectedAsset={selectedAssets.length === 1 ? selectedAssets[0] : ""}
        />
      </TableWrapper>
    </BookWrapper>
  );
}
